import {
  AnalyticsActionTypes,
  type GDDLEvent,
  type ToolTrackValues,
} from '@analytics/models';
import { inject, Injectable } from '@angular/core';
import type { RichTextField, TextField } from '@core/jss-proxy';
import { getFieldValue, sanitizeFieldValue } from '@core/jss-utils';

import { AnalyticsUtils } from '../analytics-utils.service';
import { processAnalyticsValue } from '../utils';

/**
 * Service responsible for handling tool tracking analytics and event creation.
 * This service provides utilities for generating tool metadata and creating standardized analytics events
 * for tool interactions within the application.
 */
@Injectable({
  providedIn: 'root',
})
export class ToolTrackingUtils {
  /** Analytics utilities service instance */
  readonly #utils = inject(AnalyticsUtils);

  /**
   * Generates a standardized step name for analytics tracking
   * @param values - Tool tracking values containing step information
   * @param toolName - Optional tool name to prefix the step name
   * @returns Formatted step name string
   * @private
   */
  #getStepName(values: ToolTrackValues, toolName = '') {
    const stepName = values.stepName ?? '';

    if (values.type === AnalyticsActionTypes.LAST_STEP) {
      return `${toolName} laststep`;
    }

    return [toolName, stepName].join(' ').trim().toLowerCase();
  }

  /**
   * Extracts the tool name from tracking values using various fallback options
   * @param values - Tool tracking values containing tool information
   * @returns Tool name string, defaults to 'unknown-component' if no name is found
   * @private
   */
  #getToolName(values: ToolTrackValues) {
    return (
      values.toolName ??
      getFieldValue<string>(values.rendering, 'componentName') ??
      values.rendering?.componentName ??
      'unknown-component'
    );
  }

  /**
   * Generates metadata for tool tracking events
   * @param values - Tool tracking values containing event information
   * @returns Object containing standardized tool metadata including:
   *  - name: Combined site abbreviation and tool name
   *  - placement: Combined site context and tool name
   *  - step: Current step number
   *  - stepname: Formatted step name
   */
  public async getToolMetadata(values: ToolTrackValues) {
    const toolName = this.#getToolName(values);
    const name = this.#utils.prefixWithAbbreviation(toolName);
    const placement = await this.#utils.getElementPlacement(toolName);
    return {
      name,
      placement,
      step: values.step,
      // This field is intentionally not camelCase, as making it camelcase breaks the integration with Adobe Analytics within Adobe Launch.
      stepname: this.#getStepName(values, toolName),
    };
  }

  /**
   * Creates a standardized analytics event object
   * @param event - Type of analytics action being tracked
   * @param info - Additional event information to include
   * @param metadata - Tool metadata object containing name, placement, step, and stepname
   * @returns GDDLEvent object containing the combined event information
   * @public
   */
  public createToolEvent(
    event: AnalyticsActionTypes,
    info: Record<string, any>,
    metadata: {
      name: string;
      placement: string;
      step: number;
      stepname: string;
    }
  ): GDDLEvent {
    return {
      event,
      info: {
        ...metadata,
        ...info,
      },
    };
  }

  public fieldValueAsResult(
    inputValue: string | undefined,
    values: Partial<{
      sendValue: boolean;
      hashValue: boolean;
    }>,
    label: TextField | RichTextField | string
  ) {
    const _props: { [key: string]: any } = {
      // This field is intentionally not camelCase, as making it camelcase breaks the integration with Adobe Analytics within Adobe Launch.
      fieldname: sanitizeFieldValue(label),
    };

    _props['result'] = {
      [_props['fieldname']]: processAnalyticsValue(inputValue, values),
    };

    return _props;
  }
}
